import clsx from "clsx";
import { Plus } from "lucide-react";
import { useState } from "react";
import { ErrorNotice, ListWarning, Notice, PageHeader, Panel } from "../../web/components/ui";
import { useMoney } from "../../web/lib/money";
import { rowOpen } from "../../web/lib/rowOpen";
import { basisLabel, type MoneyFormatter } from "../../shared/money";
import { can, freeGroupName, groupTaxBasis, membersOf, outOfDate, usePriceListSaver, whyNot } from "./data";
import { describePricing } from "./describe";
import { ListToolbar, matchesSearch } from "./ListToolbar";
import type { PriceListData, PricingGroupRow, PricingRule } from "./meta";
import { LockMark } from "./PriceLock";
import { attentionByGroup, attentionText } from "./attention";

/*
 * Groups: items sharing one pricing (and name tags), changed once for all of them. Each
 * opens in its own window; a new one starts as cost + 0%, named "New group" for now, its window
 * opening with the name ready to type over. Groups whose items' prices or names don't match yet
 * are flagged (they're updated from the group's window).
 */

type Filter = "all" | "update";
/** The Show list (ListToolbar), as on the other lists. */
const SHOW_LABELS: Record<Filter, string> = { all: "All groups", update: "Items to update or needing attention" };
const SHOW_GROUPS: { label: string | null; filters: Filter[] }[] = [
    { label: null, filters: ["all"] },
    { label: "Needs attention", filters: ["update"] },
];
const FILTERS = Object.keys(SHOW_LABELS) as Filter[];

/** A new group's pricing: set in its window, which opens straight away. */
const NEW_GROUP_RULE: PricingRule = { mode: "percent", markupValue: "0", basePrice: "0", labourMinutes: 0, labourUnit: "minutes", roundMode: "none", roundTo: "0" };

export function GroupsList(props: Readonly<{ data: PriceListData; openId?: string; onOpen: (id: string, options?: { rename?: boolean }) => void }>) {
    const { data } = props;
    const fmt = useMoney();
    const saver = usePriceListSaver();
    // A link can open it already searched (e.g. "See all" from Home's search): ?q=...
    const [query, setQuery] = useState(() => new URLSearchParams(globalThis.location.search).get("q") ?? "");
    const [filter, setFilter] = useState<Filter>("all");
    const [creating, setCreating] = useState(false);
    const [error, setError] = useState<Error | null>(null);
    // Its items needing attention (the same checks as the Materials list), every group's at once.
    const attention = attentionByGroup(data);
    const groups = [...data.pricingGroups]
        .sort((a, b) => a.name.localeCompare(b.name))
        .map((g) => ({ g, items: membersOf(data, g.id).length, stale: outOfDate(data, g), attention: attention.get(g.id) ?? null }));
    const inFilter = (f: Filter, row: (typeof groups)[number]) => f === "all" || row.stale.prices + row.stale.costs + row.stale.names + row.stale.noCost > 0 || row.attention !== null;
    const counts = Object.fromEntries(FILTERS.map((f) => [f, groups.filter((row) => inFilter(f, row)).length])) as Record<Filter, number>;
    const rows = groups.filter((row) => inFilter(filter, row) && matchesSearch(`${row.g.name} ${row.g.description}`, query));
    const newGroup = () => {
        setCreating(true);
        setError(null);
        saver
            .createGroup(freeGroupName(data, "New group"), NEW_GROUP_RULE)
            .then((g) => props.onOpen(g.id, { rename: true }))
            .catch((err: Error) => setError(err))
            .finally(() => setCreating(false));
    };

    // A group's pricing (cost plus a markup) gives its items' costs away: only with See costs.
    if (!can(data, "costs.view")) {
        return (
            <div className="page is-wide">
                <PageHeader title="Groups" subtitle="Items sharing one pricing and name tags: change it once, for all of them." />
                <Notice>{whyNot(data, "costs.view")}</Notice>
            </div>
        );
    }

    return (
        <div className="page is-wide">
            <PageHeader
                title="Groups"
                subtitle="Items sharing one pricing and name tags: change it once, for all of them."
                actions={
                    can(data, "groups") ? (
                        <button type="button" className="btn-primary" disabled={creating} onClick={newGroup}>
                            <Plus className="size-4" /> New group
                        </button>
                    ) : null
                }
            />
            {error ? (
                <div className="mb-4">
                    <ErrorNotice error={error} />
                </div>
            ) : null}
            <Panel flush>
                <ListToolbar
                    searchLabel="Search groups"
                    placeholder="Search by name"
                    query={query}
                    onQuery={setQuery}
                    filter={filter}
                    onFilter={setFilter}
                    groups={SHOW_GROUPS}
                    labels={SHOW_LABELS}
                    counts={counts}
                />
                <table className="list is-fixed min-w-[720px]">
                    <colgroup>
                        <col className="w-[34%]" />
                        <col className="w-[36%]" />
                        <col className="w-[30%]" />
                    </colgroup>
                    <thead>
                        <tr>
                            <th>Group</th>
                            <th>Pricing</th>
                            <th className="text-right">Items</th>
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map(({ g, items, stale }) => (
                            <GroupRow
                                key={g.id}
                                group={g}
                                pricing={groupPricing(props.data, g, fmt)}
                                items={items}
                                stale={stale}
                                attention={attention.get(g.id) ?? null}
                                open={props.openId === g.id}
                                onOpen={() => props.onOpen(g.id)}
                            />
                        ))}
                    </tbody>
                </table>
                {rows.length === 0 ? <p className="p-6 text-center text-sm text-muted">{data.pricingGroups.length ? "No groups match." : "No groups yet."}</p> : null}
            </Panel>
        </div>
    );
}

/** A group's pricing in a few words, its amounts as it taxes its items: "$100.00 base · inc GST". */
function groupPricing(data: PriceListData, g: PricingGroupRow, fmt: MoneyFormatter) {
    // Not setting pricing: each item's own (its column says so, in a word).
    if (!g.setsPricing) return g.setsTags ? "Name tags only" : "Neither (a group of items)";
    const taxed = groupTaxBasis(data, g.tax);
    return `${describePricing(g.rule, taxed, fmt)} · ${basisLabel(taxed.includesTax, taxed.rate)}`;
}

/** A group: its name, its pricing, and how many items (with what of theirs doesn't match it yet). */
function GroupRow(
    props: Readonly<{
        group: PricingGroupRow;
        pricing: string;
        items: number;
        stale: ReturnType<typeof outOfDate>;
        /** Its items needing attention (attention.ts), or null: none. */
        attention: { count: number; danger: boolean } | null;
        open: boolean;
        onOpen: () => void;
    }>,
) {
    const { group, stale, attention } = props;
    // What of its items doesn't match it yet, or is to update ("2 prices", "1 cost", "1 name", "1 no cost").
    const outOfStep = [
        stale.prices ? `${stale.prices} ${stale.prices === 1 ? "price" : "prices"}` : "",
        stale.costs ? `${stale.costs} ${stale.costs === 1 ? "cost" : "costs"}` : "",
        stale.names ? `${stale.names} ${stale.names === 1 ? "name" : "names"}` : "",
        stale.noCost ? `${stale.noCost} no cost` : "",
    ].filter(Boolean);
    return (
        // The name button covers the whole row (after:inset-0): click selects it, double-click opens the group.
        <tr className={clsx("relative", attention?.danger ? "is-danger" : (outOfStep.length > 0 || attention) && "is-warning", props.open ? "bg-brand-500/10" : "hover:bg-surface-2 focus-within:bg-brand-500/5")}>
            <td className="h-14">
                <button type="button" className="block w-full min-w-0 cursor-default text-left outline-none after:absolute after:inset-0" {...rowOpen(props.onOpen)}>
                    <span className="flex min-w-0 items-center gap-1.5">
                        <span className="truncate font-semibold">{group.name}</span>
                        <LockMark lock={group.locksPrices ? { by: "group", group: group.name } : null} />
                    </span>
                </button>
            </td>
            <td className="truncate text-muted">{props.pricing}</td>
            <td className="text-right">
                <span className="inline-flex items-center justify-end gap-1.5">
                    {attention ? (
                        <ListWarning tone={attention.danger ? "danger" : "warning"} title={`${attentionText(attention.count)}: open the group to see them.`}>
                            {attentionText(attention.count)}
                        </ListWarning>
                    ) : null}
                    {outOfStep.length > 0 ? (
                        <ListWarning title="Items that don't match the group yet (prices, names), costs to update, or no cost to work a price out from. Open the group to update them.">
                            {outOfStep.join(" · ")}
                        </ListWarning>
                    ) : null}
                    <span className="tabular-nums">{props.items.toLocaleString()}</span>
                </span>
            </td>
        </tr>
    );
}
